import type { AppContext } from '@application/context/AppContext';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { logger } from '@infra/telemetry/Logger';
import type { Salarie } from '@shared/types/Salarie';
import type { User } from '@shared/types/User';
import { getTenantUserById, listTenantUsers } from './UserDirectoryService';

/** Forme canonique d'un e-mail : la casse et les espaces ne doivent pas empêcher un rattachement. */
export function normalizeEmail(email: string | null | undefined): string {
  return (email ?? '').trim().toLowerCase();
}

/** Fiches portant cet e-mail. Plusieurs résultats = ambiguïté, aucun rattachement automatique. */
function salariesWithEmail(salaries: Salarie[], email: string | null | undefined): Salarie[] {
  const cible = normalizeEmail(email);
  if (!cible) return [];
  return salaries.filter((s) => normalizeEmail(s.email) === cible);
}

/** Fiche unique portant cet e-mail, s'il n'y a pas d'ambiguïté. */
export function findSalarieByEmail(
  salaries: Salarie[],
  email: string | null | undefined,
): Salarie | undefined {
  const candidats = salariesWithEmail(salaries, email);
  return candidats.length === 1 ? candidats[0] : undefined;
}

/** Fiche salarié d'un compte : lien explicite d'abord, correspondance d'e-mail en repli. */
export function resolveSalarieForUser(
  user: User | null | undefined,
  salaries: Salarie[],
): Salarie | undefined {
  if (!user) return undefined;
  if (user.salarie_id) {
    const parLien = salaries.find((s) => s.id === user.salarie_id);
    if (parLien) return parLien;
  }
  return findSalarieByEmail(salaries, user.email);
}

/**
 * Écrit le lien sur le compte.
 *
 * En multi-postes l'annuaire est porté par business-api (`PATCH /v1/users/:id` + `salarie_id`).
 * Si l'écriture distante échoue, la correspondance par e-mail reste le repli à la lecture.
 */
async function persistLink(ctx: AppContext, userId: string, salarieId: string): Promise<boolean> {
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    try {
      const { getBusinessApiClient } = await import('@infra/api/BusinessApiClient');
      await getBusinessApiClient().updateUser(userId, { salarie_id: salarieId });
      return true;
    } catch (error) {
      logger.warn({ event: 'salarie_link_remote_failed', error: String(error) });
      return false;
    }
  }
  const stored = await ctx.localTables.get<User>('users', userId);
  if (!stored || stored.salarie_id === salarieId) return false;
  await ctx.localTables.put<User>('users', { ...stored, salarie_id: salarieId });
  return true;
}

/**
 * Fiche salarié de l'utilisateur connecté.
 *
 * Le lien manquant est réparé au passage : les comptes créés avant leur fiche
 * (ou l'inverse) se rattachent dès la première consultation de l'espace personnel.
 */
export async function resolveCurrentSalarie(
  ctx: AppContext,
  salaries?: Salarie[],
): Promise<Salarie | undefined> {
  const session = ctx.auth.getSession();
  if (!session) return undefined;
  const liste = salaries ?? (await ctx.salaries.list());
  const user = await getTenantUserById(ctx, session.user_id, ctx.tenantContext.tryGetTenant()?.id);
  if (!user) return undefined;
  const parEmail = findSalarieByEmail(liste, user.email);
  if (user.salarie_id && parEmail && user.salarie_id !== parEmail.id) {
    const linked = liste.find((s) => s.id === user.salarie_id);
    const emailLien = normalizeEmail(linked?.email);
    const emailCompte = normalizeEmail(user.email);
    if (emailLien && emailCompte && emailLien !== emailCompte) {
      await persistLink(ctx, user.id, parEmail.id);
      return parEmail;
    }
  }
  const salarie = resolveSalarieForUser(user, liste);
  if (user && salarie && user.salarie_id !== salarie.id) {
    await persistLink(ctx, user.id, salarie.id);
  }
  return salarie;
}

/** Rattache un compte de type salarié à la fiche portant le même e-mail. */
export async function linkUserToMatchingSalarie(
  ctx: AppContext,
  user: User,
): Promise<Salarie | undefined> {
  if (user.role !== 'salarie' || user.salarie_id) return undefined;
  const salaries = await ctx.salaries.list();
  const salarie = findSalarieByEmail(salaries, user.email);
  if (!salarie) {
    if (salariesWithEmail(salaries, user.email).length > 1) {
      logger.warn({ event: 'salarie_link_ambiguous', email: normalizeEmail(user.email) });
    }
    return undefined;
  }
  await persistLink(ctx, user.id, salarie.id);
  return salarie;
}

/** Rattache une fiche nouvellement créée au compte salarié portant le même e-mail. */
export async function linkSalarieToMatchingUser(
  ctx: AppContext,
  salarie: Salarie,
): Promise<User | undefined> {
  const cible = normalizeEmail(salarie.email);
  if (!cible) return undefined;
  const users = await listTenantUsers(ctx, salarie.tenant_id);
  const candidats = users.filter(
    (u) => u.role === 'salarie' && normalizeEmail(u.email) === cible && !u.salarie_id,
  );
  if (candidats.length !== 1) {
    if (candidats.length > 1) {
      logger.warn({ event: 'user_link_ambiguous', email: cible });
    }
    return undefined;
  }
  const user = candidats[0]!;
  await persistLink(ctx, user.id, salarie.id);
  return user;
}
